import { LMSProvider } from '../types/lms';

export interface CanvasUser {
  id: number;
  name: string;
  short_name: string;
  sortable_name: string;
  email: string;
  login_id: string;
}

export interface CanvasCourse {
  id: number;
  name: string;
  course_code: string;
  start_at: string;
  end_at: string;
  enrollments: CanvasEnrollment[];
  total_students: number;
}

export interface CanvasEnrollment {
  id: number;
  user_id: number;
  course_id: number;
  type: 'StudentEnrollment' | 'TeacherEnrollment' | 'TaEnrollment';
  enrollment_state: 'active' | 'invited' | 'inactive';
  user: CanvasUser;
}

export interface CanvasAttendance {
  user_id: number;
  status: 'present' | 'absent' | 'tardy' | 'excused';
  date: string;
  class_date: string;
  section_id?: number;
}

export interface CanvasGrade {
  assignment_id: number;
  user_id: number;
  current_score: number;
  final_score: number;
  current_grade: string;
  final_grade: string;
  workflow_state: 'submitted' | 'graded' | 'pending_review';
}

export interface CanvasAssignment {
  id: number;
  name: string;
  description: string;
  due_at: string;
  points_possible: number;
  grading_type: string;
  submission_types: string[];
  workflow_state: 'published' | 'unpublished';
}

export class CanvasService {
  private provider: LMSProvider;

  constructor(provider: LMSProvider) {
    this.provider = provider;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.provider.apiUrl}/api/v1${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.provider.apiKey}`,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Canvas API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // Get all courses for the current user/account
  async getCourses(params?: {
    enrollment_type?: 'teacher' | 'student' | 'ta';
    state?: 'available' | 'completed' | 'deleted';
    include?: string[];
  }): Promise<CanvasCourse[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.enrollment_type) {
      searchParams.append('enrollment_type', params.enrollment_type);
    }
    if (params?.state) {
      searchParams.append('state[]', params.state);
    }
    if (params?.include) {
      params.include.forEach(item => searchParams.append('include[]', item));
    }

    const endpoint = `/courses?${searchParams.toString()}`;
    return this.makeRequest<CanvasCourse[]>(endpoint);
  }

  // Get course enrollments
  async getCourseEnrollments(courseId: number, params?: {
    type?: string[];
    state?: string[];
    include?: string[];
  }): Promise<CanvasEnrollment[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.type) {
      params.type.forEach(type => searchParams.append('type[]', type));
    }
    if (params?.state) {
      params.state.forEach(state => searchParams.append('state[]', state));
    }
    if (params?.include) {
      params.include.forEach(item => searchParams.append('include[]', item));
    }

    const endpoint = `/courses/${courseId}/enrollments?${searchParams.toString()}`;
    return this.makeRequest<CanvasEnrollment[]>(endpoint);
  }

  // Get course assignments
  async getCourseAssignments(courseId: number, params?: {
    include?: string[];
    search_term?: string;
    override_assignment_dates?: boolean;
  }): Promise<CanvasAssignment[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.include) {
      params.include.forEach(item => searchParams.append('include[]', item));
    }
    if (params?.search_term) {
      searchParams.append('search_term', params.search_term);
    }
    if (params?.override_assignment_dates) {
      searchParams.append('override_assignment_dates', 'true');
    }

    const endpoint = `/courses/${courseId}/assignments?${searchParams.toString()}`;
    return this.makeRequest<CanvasAssignment[]>(endpoint);
  }

  // Get grades for a course
  async getCourseGrades(courseId: number, params?: {
    student_ids?: number[];
    assignment_ids?: number[];
    include?: string[];
  }): Promise<CanvasGrade[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.student_ids) {
      params.student_ids.forEach(id => searchParams.append('student_ids[]', id.toString()));
    }
    if (params?.assignment_ids) {
      params.assignment_ids.forEach(id => searchParams.append('assignment_ids[]', id.toString()));
    }
    if (params?.include) {
      params.include.forEach(item => searchParams.append('include[]', item));
    }

    const endpoint = `/courses/${courseId}/students/submissions?${searchParams.toString()}`;
    return this.makeRequest<CanvasGrade[]>(endpoint);
  }

  // Custom attendance API (if Canvas has attendance tracking enabled)
  async getCourseAttendance(courseId: number, date?: string): Promise<CanvasAttendance[]> {
    const searchParams = new URLSearchParams();
    if (date) {
      searchParams.append('start_date', date);
      searchParams.append('end_date', date);
    }

    const endpoint = `/courses/${courseId}/roll_call_attendance?${searchParams.toString()}`;
    
    try {
      return this.makeRequest<CanvasAttendance[]>(endpoint);
    } catch (error) {
      // If roll call attendance is not available, return empty array
      console.warn('Canvas attendance API not available:', error);
      return [];
    }
  }

  // Submit attendance for a course
  async submitAttendance(courseId: number, attendanceData: {
    user_id: number;
    status: 'present' | 'absent' | 'tardy' | 'excused';
    date: string;
  }[]): Promise<boolean> {
    try {
      for (const attendance of attendanceData) {
        const endpoint = `/courses/${courseId}/roll_call_attendance`;
        await this.makeRequest(endpoint, {
          method: 'POST',
          body: JSON.stringify({
            user_id: attendance.user_id,
            status: attendance.status,
            class_date: attendance.date,
          }),
        });
      }
      return true;
    } catch (error) {
      console.error('Error submitting attendance to Canvas:', error);
      return false;
    }
  }

  // Get user profile
  async getUser(userId: number): Promise<CanvasUser> {
    const endpoint = `/users/${userId}/profile`;
    return this.makeRequest<CanvasUser>(endpoint);
  }

  // Get current user profile
  async getCurrentUser(): Promise<CanvasUser> {
    const endpoint = '/users/self/profile';
    return this.makeRequest<CanvasUser>(endpoint);
  }

  // Test connection to Canvas
  async testConnection(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      console.error('Canvas connection test failed:', error);
      return false;
    }
  }

  // Sync all course data
  async syncAllCourseData(courseId: number): Promise<{
    course: CanvasCourse;
    enrollments: CanvasEnrollment[];
    assignments: CanvasAssignment[];
    attendance: CanvasAttendance[];
  }> {
    try {
      const [courses, enrollments, assignments, attendance] = await Promise.all([
        this.getCourses({ state: 'available' }),
        this.getCourseEnrollments(courseId, { 
          type: ['StudentEnrollment', 'TeacherEnrollment'],
          state: ['active'],
          include: ['user']
        }),
        this.getCourseAssignments(courseId, { 
          include: ['submission'] 
        }),
        this.getCourseAttendance(courseId)
      ]);

      const course = courses.find(c => c.id === courseId);
      if (!course) {
        throw new Error(`Course ${courseId} not found`);
      }

      return {
        course,
        enrollments,
        assignments,
        attendance
      };
    } catch (error) {
      console.error('Error syncing Canvas course data:', error);
      throw error;
    }
  }

  // Get analytics data for a course
  async getCourseAnalytics(courseId: number): Promise<{
    student_summaries: Array<{
      id: number;
      page_views: number;
      max_page_views: number;
      participations: number;
      max_participations: number;
      tardiness_breakdown: Record<string, number>;
    }>;
  }> {
    const endpoint = `/courses/${courseId}/analytics/student_summaries`;
    return this.makeRequest(endpoint);
  }

  // Get course activity stream
  async getCourseActivityStream(courseId: number): Promise<Array<{
    id: number;
    title: string;
    message: string;
    type: string;
    created_at: string;
    updated_at: string;
    context_type: string;
    course_id: number;
  }>> {
    const endpoint = `/courses/${courseId}/activity_stream`;
    return this.makeRequest(endpoint);
  }

  // Create a Canvas assignment
  async createAssignment(courseId: number, assignment: {
    name: string;
    description?: string;
    due_at?: string;
    points_possible?: number;
    grading_type?: 'pass_fail' | 'percent' | 'letter_grade' | 'gpa_scale' | 'points';
    submission_types?: string[];
    published?: boolean;
  }): Promise<CanvasAssignment> {
    const endpoint = `/courses/${courseId}/assignments`;
    return this.makeRequest<CanvasAssignment>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ assignment }),
    });
  }

  // Update assignment grades
  async updateGrade(courseId: number, assignmentId: number, userId: number, grade: {
    posted_grade?: string;
    rubric_assessment?: Record<string, any>;
    comment?: string;
  }): Promise<any> {
    const endpoint = `/courses/${courseId}/assignments/${assignmentId}/submissions/${userId}`;
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ submission: grade }),
    });
  }

  // Send a message to course participants
  async sendMessage(courseId: number, message: {
    recipients: number[];
    subject: string;
    body: string;
    context_code?: string;
  }): Promise<any> {
    const endpoint = '/conversations';
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        recipients: message.recipients.map(id => `course_${courseId}_${id}`),
        subject: message.subject,
        body: message.body,
        context_code: message.context_code || `course_${courseId}`,
        group_conversation: true,
      }),
    });
  }
}

export default CanvasService;